// BillingModal.js
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, Button, Box
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { paymentStatus, paymentType, SALE_ORIGIN, resolveChannelPrice } from '../../../../utils/Enums';
import UndefinedContentModal from './UndefinedContentModal';
import StepClient from './StepClient';
import StepSummary from './StepSummary';
import SplitPaymentPanel from './SplitPaymentPanel';
import { awaitingRows, buildPaymentsPayload, collectedAmount, initialParts, isPosPart, newPart, round2, splitError, SPLIT_METHODS } from './splitPayments';
import StepAwaitingPayment from './StepAwaitingPayment';
import { posFailure } from './posFailure';
import StepCashPayment from './StepCashPayment';
import { createInvoice, getInvoiceStatus, fetchInvoiceTickets, getStoreCredit } from '../../../../services/SalesService';
import { getLocationSalesConfig } from '../../../../services/SettingsService';
import { getPosAvailability, getUnbilledPosPayments, startPosPayment, pollPosPayment, waitForPosPayment, PosPaymentTimeoutError } from '../../../../services/PosPaymentService';
import { isValidEmail } from '../../../../utils/ValidatorUtils';
import { base64ToBlob } from './../../../../utils/ImageUtils'
import PlatformBridge from '../../../../utils/PlatformBridge';
import SnackbarUtils from '../../../../utils/SnackbarUtils';
import { hasStoredPermission } from '../../../../utils/dashboardPermissions';
import { formatMoney } from '../../../../utils/currency';

import { ticketCurrency } from '../ticketCurrency';
import { useCurrencies } from '../../../../context/CurrencyContext';
import { CURRENCIES, DEFAULT_CURRENCY, getCurrency } from '../../../../utils/currency';
const normalizeRut = (value) => (value || '').replace(/\D/g, '');

const isSimpleRutSequence = (value) => (
  '01234567890123456789'.includes(value) ||
  '98765432109876543210'.includes(value)
);

const isValidRut = (value) => {
  const rut = normalizeRut(value);
  if (rut.length !== 12) return false;
  if (rut.split('').every((digit) => digit === rut[0])) return false;
  if (isSimpleRutSequence(rut)) return false;

  const weights = [4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const sum = rut.slice(0, 11).split('').reduce(
    (acc, digit, index) => acc + Number(digit) * weights[index],
    0
  );
  const candidate = 11 - (sum % 11);
  const expected = candidate > 9 ? 0 : candidate;
  return expected === Number(rut[11]);
};

// createSale / disableStoreCredit / lockOrigin permiten reusar el flujo de cobro del POS desde otra pantalla (ej. cobro de
// mesas): createSale recibe el mismo saleData y devuelve la factura; el origen queda fijo en local y sin crédito de tienda.
const BillingModal = ({ locationId, open, onClose, items, discountCode, initialStoreCreditCode = null, onStoreCreditConsumed, onStoreCreditChange, onFinish, createSale = createInvoice, disableStoreCredit = false, lockOrigin = false }) => {
  const currency = ticketCurrency(items);
  const { rateViews } = useCurrencies();
  const ticketRate = rateViews.find((view) => view.currency === currency)?.rate;
  const canApplyStoreCredit = hasStoredPermission('APPLY_STORE_CREDIT') && !disableStoreCredit;
  const CASH_PAYMENT_METHOD_ID = 1;
  const [activeStep, setActiveStep] = useState(0);
  const [needsCompany, setNeedsCompany] = useState(false);
  const [adenda, setAdenda] = useState('');
  const [clientInfo, setClientInfo] = useState({ ci: '', name: '', lastName: '', email: '', phone: '' });
  const [companyInfo, setCompanyInfo] = useState({
    ci: '',
    name: '',
    fiscalAddress: '',
    lookupStatus: 'idle',
    lookupSource: null,
    manualRegistrationConfirmed: false,
  });
  const [errors, setErrors] = useState({});
  const [skipClient, setSkipClient] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [showUndefinedModal, setShowUndefinedModal] = useState(false);
  const [undefinedProducts, setUndefinedProducts] = useState([]);
  const [savedInvoice, setSavedInvoice] = useState(null);
  const [polling, setPolling] = useState(false);
  const [mpActive, setMpActive] = useState(false);
  const [posActive, setPosActive] = useState(false);
  const [canCancel, setCanCancel] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [saleOrigin, setSaleOrigin] = useState(SALE_ORIGIN.LOCAL);
  const [cashReceived, setCashReceived] = useState('');
  // Ticket en otra moneda: se cobra en esa moneda o en pesos (con Mercado Pago, siempre pesos).
  const [chosenPaymentCurrency, setChosenPaymentCurrency] = useState(null);
  // The POS provider the terminal sells with (descriptor from /payments/pos/availability), or null when there is none.
  const [posProvider, setPosProvider] = useState(null);
  const posEnabled = posProvider !== null;
  const [posTransaction, setPosTransaction] = useState(null);
  // Inline notice shown in the summary step when a card payment did not go through.
  const [paymentNotice, setPaymentNotice] = useState(null);
  // Progress reported by the POS while a card payment is open (PENDING, IN_PROGRESS...).
  const [posProgress, setPosProgress] = useState(null);
  // Card payments approved at the POS that never got an invoice, so the cashier can bill them.
  const [unbilledPayments, setUnbilledPayments] = useState([]);
  // POS payment already approved but not yet invoiced ({ id, amount }): a retry must reuse it, never charge again.
  const approvedPayment = useRef(null);
  // A POS authorization whose outcome we could not confirm (timeout, network). It is resumed -- same
  // transaction / idempotency key -- instead of starting a new charge, until it reaches a final state.
  const pendingPayment = useRef(null);
  // Identifies one checkout session; renewed every time the modal opens or closes.
  const checkoutSession = useRef(null);
  // Pago dividido: la venta se paga con varios medios (cada uno cubre un monto). Cada parte lleva su propio cobro del POS.
  const [splitMode, setSplitMode] = useState(false);
  const [splitParts, setSplitParts] = useState([]);
  const splitPartsRef = useRef([]);
  // La parte de un pago dividido que el POS tiene en curso (para el resumen de la espera).
  const [currentPosPartUid, setCurrentPosPartUid] = useState(null);
  const [storeCreditCode, setStoreCreditCode] = useState('');
  const [storeCreditAmount, setStoreCreditAmount] = useState(0);
  const [storeCreditError, setStoreCreditError] = useState('');
  const [storeCreditLoading, setStoreCreditLoading] = useState(false);

  // A new sale must never inherit the previous sale's payment choice.
  // Likewise a POS payment assigned to a previous checkout: the cashier must pick the unbilled charge again.
  useEffect(() => {
    approvedPayment.current = null;
    checkoutSession.current = typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `checkout-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setPaymentNotice(null);
    if (!open) return;
    setPaymentMethod('');
  }, [open]);

  useEffect(() => {
    if (!initialStoreCreditCode) {
      setStoreCreditCode('');
      setStoreCreditAmount(0);
      setStoreCreditError('');
      return;
    }
    setStoreCreditCode(initialStoreCreditCode);
  }, [initialStoreCreditCode]);

  // A permission refresh can revoke credit application while the modal remains
  // open. Never retain a previously validated amount in that case.
  useEffect(() => {
    if (canApplyStoreCredit) return;
    setStoreCreditCode('');
    setStoreCreditAmount(0);
    setStoreCreditError('');
    setStoreCreditLoading(false);
    onStoreCreditConsumed?.();
  }, [canApplyStoreCredit, onStoreCreditConsumed]);

  useEffect(() => {
    if (!locationId) return;
    const terminalCode = localStorage.getItem('terminalId');
    getLocationSalesConfig(locationId).then((config) => {
      setMpActive(config.mercadoPagoActive);
      setPosActive(config.posActive);
    }).catch(() => {
      setMpActive(false);
      setPosActive(false);
    });
    getPosAvailability(locationId, terminalCode)
      .then((availability) => setPosProvider(availability.enabled ? availability.provider ?? { code: null, displayName: 'POS' } : null))
      .catch(() => setPosProvider(null));
  }, [locationId]);

  const refreshUnbilledPayments = () => {
    const terminalCode = localStorage.getItem('terminalId');
    getUnbilledPosPayments(locationId, terminalCode)
      .then(setUnbilledPayments)
      .catch(() => setUnbilledPayments([]));
  };

  useEffect(() => {
    if (open && posEnabled) refreshUnbilledPayments();
  }, [open, posEnabled, locationId]);

  // Bills an approved-but-unbilled POS payment: it is charged once, never again.
  const useUnbilledPayment = (payment) => {
    if (pendingPayment.current?.id === payment.transactionId) pendingPayment.current = null;
    approvedPayment.current = { id: payment.transactionId, amount: Number(payment.amount) };
    // El cobro ya se hizo en una moneda (ISO numérico): la venta se factura igual, pero se registra en esa moneda.
    const chargedCurrency = Object.values(CURRENCIES).find((c) => c.isoNumeric === payment.currency)?.code;
    if (chargedCurrency) setChosenPaymentCurrency(chargedCurrency);
    setPaymentNotice({
      severity: 'success',
      title: 'Cobro asignado a esta venta',
      message: 'Tocá Siguiente para emitir la factura. No se volverá a cobrar con el POS.',
    });
    setPaymentMethod('2');
  };

  const applySplitParts = (parts) => {
    splitPartsRef.current = parts;
    setSplitParts(parts);
  };

  const toggleSplit = () => {
    if (splitMode) {
      // Un cobro del POS ya aprobado no se puede abandonar a mitad de camino: quedaría cobrado sin venta.
      if (splitPartsRef.current.some((part) => part.transactionId)) {
        setPaymentNotice({
          severity: 'warning',
          title: 'Hay un cobro aprobado en el POS',
          message: 'Una parte ya se cobró con el POS: terminá la venta dividida para no cobrarla dos veces.',
        });
        return;
      }
      applySplitParts([]);
      setSplitMode(false);
      return;
    }
    setPaymentNotice(null);
    applySplitParts(initialParts(round2(amountDue), currency, posEnabled));
    setSplitMode(true);
  };

  // Un cobro aprobado del POS que quedó sin facturar entra como una de las partes (ya está cobrado: no se vuelve a cobrar).
  const useUnbilledAsSplitPart = (payment) => {
    const chargedCurrency = Object.values(CURRENCIES).find((c) => c.isoNumeric === payment.currency)?.code || DEFAULT_CURRENCY;
    const inTicketCurrency = chargedCurrency === currency ? Number(payment.amount) : round2(Number(payment.amount) / ticketRate);
    if (pendingPayment.current?.id === payment.transactionId) pendingPayment.current = null;
    const part = { ...newPart(SPLIT_METHODS.CARD, inTicketCurrency, chargedCurrency), transactionId: payment.transactionId };
    applySplitParts([...splitPartsRef.current.filter((existing) => Number(existing.amount) > 0 || existing.transactionId), part]);
    setPaymentNotice(null);
  };

  const printBase64 = async (base64) => {
    PlatformBridge.printPdf(base64)
      .then(() => console.log("✅ Impresión enviada"))
      .catch((e) => {
        console.error("❌ Error al imprimir:", e);
        alert("Error al imprimir. Verifique que la impresora esté conectada y configurada correctamente.");
      });
  };

  const isSplit = () => splitMode && amountDue > 0 && parseInt(saleOrigin) === SALE_ORIGIN.LOCAL;
  // Con el pago dividido el medio elegido en el selector único no cuenta: cada parte tiene el suyo.
  const isQrPaymentAsync = () => !isSplit() && parseInt(paymentMethod) === 4 && mpActive && parseInt(saleOrigin) === 1;
  const isCashPayment = () => !isSplit() && parseInt(paymentMethod) === CASH_PAYMENT_METHOD_ID;
  const isCardPayment = () => !isSplit() && !isCashPayment() && !isQrPaymentAsync() && [2, 3].includes(parseInt(paymentMethod));
  // Nothing left to collect (e.g. store credit covers the sale): the POS is not involved.
  const isPosCardPayment = () => posEnabled && isCardPayment() && amountDue > 0;
  // Pago dividido con alguna parte que se cobra en el POS (las partes aprobadas ya no vuelven a cobrarse).
  const splitHasPos = () => isSplit() && splitParts.some((part) => isPosPart(part, posEnabled));
  const isAsyncPayment = () => isQrPaymentAsync() || isPosCardPayment() || splitHasPos();

  // Recalcula el precio unitario de cada ítem según el canal de venta elegido, preservando
  // el extra de opcionales que ya traía cargado (no cambia con el origen de la venta).
  const effectiveItems = useMemo(() => items.map((item) => {
    const channelPrice = resolveChannelPrice(item, saleOrigin);
    if (channelPrice == null) return item;
    const unitPrice = channelPrice + Number(item.extrasAmount || 0);
    return unitPrice === item.unitPrice ? item : { ...item, unitPrice };
  }), [items, saleOrigin]);

  const saleTotal = effectiveItems.reduce((sum, item) => {
    const unit = Number(item.unitPrice || item.price || 0);
    const discount = Number(item.discountAmount || 0);
    return sum + (unit - discount) * Number(item.qty || 0);
  }, 0);
  const appliedStoreCreditAmount = canApplyStoreCredit ? storeCreditAmount : 0;
  const amountDue = Math.max(0, saleTotal - appliedStoreCreditAmount);
  const storeCreditPending = canApplyStoreCredit && Boolean(initialStoreCreditCode) && !storeCreditError && storeCreditAmount === 0;
  const paymentSelectionComplete = !storeCreditLoading && !storeCreditPending && (
    amountDue === 0 || (isSplit() ? splitError(splitParts, amountDue) === null : Boolean(paymentMethod))
  );

  useEffect(() => {
    if (open) onStoreCreditChange?.(appliedStoreCreditAmount);
  }, [open, appliedStoreCreditAmount, onStoreCreditChange]);

  const applyStoreCredit = async (code = storeCreditCode) => {
    if (!canApplyStoreCredit) {
      setStoreCreditAmount(0);
      setStoreCreditError('No tenés permiso para aplicar saldo de notas de crédito.');
      return;
    }
    if (!code.trim()) { setStoreCreditAmount(0); setStoreCreditError(''); return; }
    setStoreCreditLoading(true);
    try {
      const credit = await getStoreCredit(code.trim());
      const available = Number(credit?.remainingBalance || 0);
      if (available <= 0) throw new Error('La nota de crédito no tiene saldo disponible.');
      // Una nota de crédito solo se aplica a ventas en su misma moneda (no hay conversión de saldos).
      if ((credit?.currency || DEFAULT_CURRENCY) !== currency) {
        throw new Error(`La nota de crédito es en ${credit?.currency || DEFAULT_CURRENCY} y esta venta en ${currency}.`);
      }
      setStoreCreditAmount(Math.min(available, saleTotal));
      setStoreCreditError('');
    } catch (error) {
      setStoreCreditAmount(0);
      const status = error?.response?.status;
      setStoreCreditError(
        status === 404
          ? 'No encontramos una nota de crédito con ese código. Ingresá el código NC-… de la devolución, no el número de factura.'
          : error?.response?.data?.error_message || error?.message || 'No se pudo validar la nota de crédito.'
      );
    } finally {
      setStoreCreditLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !canApplyStoreCredit || !initialStoreCreditCode || saleTotal <= 0) return;
    applyStoreCredit(initialStoreCreditCode);
  }, [open, initialStoreCreditCode, saleTotal]);

  const parseAmount = (value) => {
    if (!value?.trim()) return 0;
    const sanitized = value.replace(/\s/g, '');
    const hasComma = sanitized.includes(',');
    const hasDot = sanitized.includes('.');
    let normalized = sanitized;

    if (hasComma && hasDot) {
      normalized =
        sanitized.lastIndexOf(',') > sanitized.lastIndexOf('.')
          ? sanitized.replace(/\./g, '').replace(',', '.')
          : sanitized.replace(/,/g, '');
    } else if (hasComma) {
      normalized = sanitized.replace(',', '.');
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parsedCashReceived = parseAmount(cashReceived);
  const foreignTicket = currency !== DEFAULT_CURRENCY && Boolean(ticketRate);
  const mpQrSelected = isQrPaymentAsync();
  // A POS that cannot charge in the ticket's currency (Mercado Pago only charges pesos) collects the card payment in pesos.
  const posChargesInPesosOnly = foreignTicket && isPosCardPayment() && !(posProvider?.currencies ?? [getCurrency(currency).isoNumeric]).includes(getCurrency(currency).isoNumeric);
  const canChoosePaymentCurrency = foreignTicket && Boolean(paymentMethod) && !mpQrSelected && !posChargesInPesosOnly;
  const paymentCurrency = foreignTicket && (mpQrSelected || posChargesInPesosOnly || chosenPaymentCurrency === DEFAULT_CURRENCY) ? DEFAULT_CURRENCY : currency;
  const payingInPesos = paymentCurrency !== currency;
  const toPaymentCurrency = (amount) => (payingInPesos ? Math.round(amount * ticketRate * 100) / 100 : amount);
  // Lo que se cobra (caja, POS) en la moneda de cobro; la factura sigue en la moneda del ticket.
  const paymentDue = toPaymentCurrency(amountDue);
  const canConfirmCashPayment = parsedCashReceived >= paymentDue;

  const hasReasonableCIFormat = (ci) => {
    const cleaned = (ci || '').replace(/\D/g, '');
    return cleaned.length >= 6 && cleaned.length <= 8;
  };

  const hasClientData = () => (
    [clientInfo.ci, clientInfo.name, clientInfo.lastName, clientInfo.email, clientInfo.phone]
      .some((value) => value?.trim())
  );

  const shouldRequireClient = () => !skipClient && (!needsCompany || hasClientData());

  const validateClient = () => {
    const newErrors = {};
    if (shouldRequireClient()) {
      if (!clientInfo.ci) newErrors.ci = 'Cédula requerida';
      else if (!hasReasonableCIFormat(clientInfo.ci)) newErrors.ci = 'Cédula inválida';
      if (!clientInfo.name && !clientInfo.firstName) newErrors.name = 'Nombre requerido';
      if (!clientInfo.lastName) newErrors.lastName = 'Apellido requerido';
      if (!clientInfo.email) newErrors.email = 'Correo requerido';
      else if (!isValidEmail(clientInfo.email)) newErrors.email = 'Correo inválido';
      if (!clientInfo.phone) newErrors.phone = 'Teléfono requerido';
    }
    if (needsCompany) {
      if (!companyInfo.ci) newErrors.company_ci = 'RUT requerido';
      else if (!isValidRut(companyInfo.ci)) newErrors.company_ci = 'RUT inválido';
      else if (companyInfo.lookupStatus !== 'found' && !companyInfo.manualRegistrationConfirmed) {
        newErrors.company_ci = 'Buscá el RUT antes de continuar';
      }
      if (!companyInfo.name) newErrors.company_name = 'Razón social requerida';
      if (!companyInfo.fiscalAddress) newErrors.company_fiscalAddress = 'Dirección fiscal requerida';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (activeStep === 0 && !validateClient()) return;
    if (activeStep === 1) {
      if (isAsyncPayment()) {
        setActiveStep(2);
        handleConfirm();
        return;
      }
      if (isCashPayment()) {
        setActiveStep(2);
        return;
      }
    }
    if (activeStep === 2 && isCashPayment()) {
      handleConfirm();
      return;
    }
    setActiveStep(prev => prev + 1);
  };

  const handleSkipClient = () => {
    setClientInfo({ ci: '', name: '', lastName: '', email: '', phone: '' });
    setErrors({});
    setSkipClient(true);
    setActiveStep((prev) => prev + 1);
  };

  // Asks the POS what became of an unconfirmed attempt. Returns true when the new payment must wait
  // (and tells the cashier why); false when the attempt ended without a charge and was released.
  const reconcileStalePending = async (terminalCode) => {
    const stale = pendingPayment.current;
    let status = null;
    if (stale.id && !stale.approved) {
      try {
        status = await pollPosPayment({
          location_id: parseInt(locationId),
          terminal_code: terminalCode,
          transaction_id: stale.id,
        });
      } catch (_) {
        status = null;
      }
    }
    if (status?.finished && !status.approved) {
      pendingPayment.current = null;
      return false;
    }
    // Approved: keep it registered (blocking any new authorization) until the cashier explicitly
    // assigns it from the unbilled list -- see useUnbilledPayment.
    if (status?.finished) stale.approved = true;
    setActiveStep(1);
    refreshUnbilledPayments();
    setPaymentNotice({
      severity: 'error',
      title: 'Hay un cobro con el POS sin resolver',
      message: stale.approved
        ? `El POS cobró ${formatMoney(stale.amount, paymentCurrency)} en un intento anterior: aparece como "sin facturar" y se puede asignar desde acá en vez de cobrar de nuevo.`
        : `Todavía no se confirmó un cobro de ${formatMoney(stale.amount, paymentCurrency)} enviado al POS. Reintentá con Tarjeta para terminar de consultarlo, o contactá a soporte: no se acepta otro pago hasta saber qué pasó.`,
    });
    return true;
  };

  // Líneas de la venta (precio final con IVA, en la moneda del ticket): el backend les asigna la tasa de cada producto para informar el IVA al POS.
  const invoiceLinesForPos = () => effectiveItems
    .map((item) => ({
      product_id: parseInt(item.id),
      amount: round2((Number(item.unitPrice || item.price || 0) - Number(item.discountAmount || 0)) * Number(item.qty || 0)),
    }))
    .filter((line) => line.amount > 0);

  // Cobra en el POS cada parte de tarjeta pendiente, una por una. Devuelve el listado de pagos para la venta, o null si
  // alguna parte no se cobró (ya se avisó al cajero y volvió al resumen). Las partes ya aprobadas no se cobran de nuevo.
  const chargeSplitParts = async () => {
    const terminalCode = localStorage.getItem('terminalId');
    const working = splitPartsRef.current.map((part) => ({ ...part }));
    const publish = () => applySplitParts(working.map((part) => ({ ...part })));
    for (const part of working) {
      if (!isPosPart(part, posEnabled) || part.transactionId) continue;
      const collected = collectedAmount(part, currency, ticketRate);
      const wholeInvoice = part.currency === currency ? saleTotal : round2(saleTotal * ticketRate);
      setCurrentPosPartUid(part.uid);
      part.idempotencyKey = part.idempotencyKey || (typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `pos-${Date.now()}-${Math.random().toString(16).slice(2)}`);
      publish();
      try {
        let transactionId = part.pendingTransactionId;
        if (transactionId) {
          setPosTransaction({ transactionId });
        } else {
          const payment = await startPosPayment({
            location_id: parseInt(locationId),
            terminal_code: terminalCode,
            amount: collected,
            invoice_amount: wholeInvoice,
            invoice_lines: invoiceLinesForPos(),
            // Factura a una empresa (con RUT): el POS no la trata como consumidor final.
            consumer_final: !needsCompany,
            currency: getCurrency(part.currency).isoNumeric,
            idempotency_key: part.idempotencyKey,
          });
          transactionId = payment.transactionId;
          part.pendingTransactionId = transactionId;
          publish();
          setPosTransaction(payment);
        }
        const result = await waitForPosPayment({
          locationId,
          terminalCode,
          transactionId,
          onProgress: (status) => setPosProgress(status?.progress || null),
        });
        setPosTransaction(null);
        setPosProgress(null);
        if (!result?.approved) {
          // Rechazo definitivo: la próxima vez es un cobro nuevo, con otra clave.
          part.pendingTransactionId = null;
          part.idempotencyKey = null;
          publish();
          const failure = posFailure(result?.message, posProvider);
          throw Object.assign(new Error(failure.message), { failure });
        }
        part.transactionId = transactionId;
        part.pendingTransactionId = null;
        publish();
      } catch (posError) {
        const httpStatus = posError?.response?.status;
        if (!part.pendingTransactionId && httpStatus >= 400 && httpStatus < 500 && ![408, 429].includes(httpStatus)) {
          part.idempotencyKey = null;
          publish();
        }
        setPosTransaction(null);
        setPosProgress(null);
        setActiveStep(1);
        refreshUnbilledPayments();
        setPaymentNotice(
          posError?.failure ||
            (posError instanceof PosPaymentTimeoutError
              ? {
                  severity: 'warning',
                  title: 'No se pudo confirmar el cobro',
                  message: 'No hubo respuesta del POS a tiempo. Tocá Siguiente para seguir consultando el mismo cobro: no se iniciará otro hasta saber si el POS cobró.',
                }
              : {
                  severity: 'error',
                  title: 'No se pudo completar el cobro',
                  message:
                    (posError?.response?.data?.error_message ||
                      posError?.response?.data?.message ||
                      posError?.message ||
                      'No se pudo completar el cobro con tarjeta.') +
                    (working.some((p) => p.transactionId) ? ' Los cobros ya aprobados en el POS se conservan y no se vuelven a cobrar.' : ''),
                })
        );
        setCurrentPosPartUid(null);
        return null;
      }
    }
    setCurrentPosPartUid(null);
    return buildPaymentsPayload(working);
  };

  const handleConfirm = async () => {
    if (isCashPayment() && !canConfirmCashPayment) {
      return;
    }
    setPaymentNotice(null);
    setLoading(true);
    try {
      const mappedClient = (skipClient || !hasClientData()) ? null : {
        ci: clientInfo.ci,
        first_name: clientInfo.name,
        last_name: clientInfo.lastName,
        email: clientInfo.email,
        phone: clientInfo.phone,
      };
      const mappedCompany = needsCompany ? {
        rut: normalizeRut(companyInfo.ci),
        name: companyInfo.name,
        fiscal_address: companyInfo.fiscalAddress
      } : null;
      const mappedItems = effectiveItems.map((item) => {
        const base = { id: parseInt(item.id), quantity: parseInt(item.qty) };
        if (item.unitPrice != null && !Number.isNaN(Number(item.unitPrice))) {
          base.unit_price = Number(item.unitPrice);
        }
        if (item.optionalSelections?.length > 0) {
          base.optional_selections = item.optionalSelections.map((s) => ({
            reference_type: s.referenceType ?? s.reference_type,
            reference_id_1: s.referenceId1 ?? s.reference_id_1
          }));
        }
        return base;
      });
      const saleData = {
        location_id: parseInt(locationId),
        terminal_code: localStorage.getItem("terminalId"),
        client: mappedClient,
        company: mappedCompany,
        purchase: {
          items: mappedItems,
          payment_method: isSplit() ? parseInt(splitParts[0].method) : parseInt(paymentMethod),
          origin: parseInt(saleOrigin),
          discount_code: discountCode || null
        },
        adenda: needsCompany && adenda ? adenda : null
      };
      if (payingInPesos && !isSplit()) {
        saleData.payment_currency = paymentCurrency;
      }
      if (canApplyStoreCredit && storeCreditAmount > 0) {
        saleData.store_credit_code = storeCreditCode.trim();
        saleData.store_credit_amount = storeCreditAmount;
      }

      // An unconfirmed charge belongs to the checkout (session, cart and customer) that started it: it is only resumed for that same sale.
      const cartKey = JSON.stringify([checkoutSession.current, mappedItems, mappedClient, mappedCompany, saleData.purchase.discount_code, saleData.purchase.origin, amountDue, paymentCurrency]);

      // Any other payment method needs the earlier POS attempt resolved first, whichever checkout started
      // it: the POS may still charge, and nothing guarantees this is a different sale.
      if (pendingPayment.current && !isPosCardPayment() && await reconcileStalePending(localStorage.getItem('terminalId'))) return;
      if (approvedPayment.current && !isPosCardPayment()) {
        // Changing to another method would leave the card charge without a sale.
        setActiveStep(1);
        setPaymentNotice({
          severity: 'error',
          title: 'Hay un cobro aprobado sin facturar',
          message: 'El POS ya cobró esta venta con tarjeta y falta emitir la factura. Elegí Tarjeta y reintentá para no cobrar dos veces.',
        });
        return;
      }
      if (isPosCardPayment() && approvedPayment.current) {
        if (Math.abs(approvedPayment.current.amount - paymentDue) > 0.001) {
          setActiveStep(1);
          setPaymentNotice({
            severity: 'error',
            title: 'Hay un cobro aprobado sin facturar',
            message: `El POS ya cobró ${formatMoney(approvedPayment.current.amount, paymentCurrency)} y no se pudo emitir la factura. Volvé a dejar la venta por ese monto, o contactá a soporte para no cobrar dos veces.`,
          });
          return;
        }
        saleData.payment_transaction_id = approvedPayment.current.id;
      } else if (isPosCardPayment()) {
        const terminalCode = localStorage.getItem('terminalId');
        if (pendingPayment.current && pendingPayment.current.cartKey !== cartKey && await reconcileStalePending(terminalCode)) return;
        if (pendingPayment.current?.approved) {
          setActiveStep(1);
          refreshUnbilledPayments();
          setPaymentNotice({
            severity: 'error',
            title: 'Hay un cobro aprobado sin asignar',
            message: `El POS ya cobró ${formatMoney(pendingPayment.current.amount, paymentCurrency)} en un intento anterior. Elegilo en la lista de cobros "sin facturar" en vez de cobrar de nuevo.`,
          });
          return;
        }
        const attempt = pendingPayment.current || {
          cartKey,
          key: typeof globalThis.crypto?.randomUUID === 'function'
            ? globalThis.crypto.randomUUID()
            : `pos-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          amount: paymentDue,
          id: null,
        };
        pendingPayment.current = attempt;
        try {
          if (attempt.id) {
            setPosTransaction({ transactionId: attempt.id });
          } else {
            const payment = await startPosPayment({
              location_id: parseInt(locationId),
              terminal_code: terminalCode,
              amount: paymentDue,
              invoice_amount: toPaymentCurrency(saleTotal),
              invoice_lines: invoiceLinesForPos(),
            // Factura a una empresa (con RUT): el POS no la trata como consumidor final.
            consumer_final: !needsCompany,
              currency: getCurrency(paymentCurrency).isoNumeric,
              idempotency_key: attempt.key,
            });
            attempt.id = payment.transactionId;
            setPosTransaction(payment);
          }
          const result = await waitForPosPayment({
            locationId,
            terminalCode,
            transactionId: attempt.id,
            onProgress: (status) => setPosProgress(status?.progress || null),
          });
          setPosTransaction(null);
          setPosProgress(null);
          pendingPayment.current = null;
          if (!result?.approved) {
            const failure = posFailure(result?.message, posProvider);
            throw Object.assign(new Error(failure.message), { failure });
          }
          saleData.payment_transaction_id = attempt.id;
          approvedPayment.current = { id: attempt.id, amount: paymentDue };
        } catch (posError) {
          const httpStatus = posError?.response?.status;
          // Only a final POS answer, or a business error before any transaction existed (and not a
          // transient 408/429), proves nothing was charged. Errors while polling a live one never do.
          if (posError?.failure || (!attempt.id && httpStatus >= 400 && httpStatus < 500 && ![408, 429].includes(httpStatus))) {
            pendingPayment.current = null;
          }
          // No sale was created: go back to the summary step so the cashier can retry or pick another
          // method. The client never cancels on its own here -- when the outcome is unknown (timeout,
          // network), the server keeps consulting the sale and cancels or records it by itself.
          setPosTransaction(null);
          setPosProgress(null);
          setActiveStep(1);
          refreshUnbilledPayments();
          setPaymentNotice(
            posError?.failure ||
              (posError instanceof PosPaymentTimeoutError
                ? {
                    severity: 'warning',
                    title: 'No se pudo confirmar el cobro',
                    message: 'No hubo respuesta del POS a tiempo. Tocá Siguiente para seguir consultando el mismo cobro: no se iniciará otro hasta saber si el POS cobró.',
                  }
                : {
                    severity: 'error',
                    title: 'No se pudo completar el cobro',
                    message:
                      (posError?.response?.data?.error_message ||
                        posError?.response?.data?.message ||
                        posError?.message ||
                        'No se pudo completar el cobro con tarjeta.') +
                      (pendingPayment.current ? ' Al reintentar se vuelve a consultar el mismo cobro, sin cobrar de nuevo.' : ''),
                  })
          );
          return;
        }
      }

      if (isSplit()) {
        const payments = await chargeSplitParts();
        if (!payments) return;
        saleData.payments = payments;
      }

      const invoice = await createSale(saleData);
      approvedPayment.current = null;
      applySplitParts([]);
      setSplitMode(false);
      refreshUnbilledPayments();
      setSavedInvoice(invoice);
      if (invoice?.cfeNumberWarning?.remaining != null) {
        SnackbarUtils.info(
          `Quedan ${invoice.cfeNumberWarning.remaining} números de CFE. Emitir nuevos.`,
          { autoHideDuration: 10000 }
        );
      }

      // The sale is already saved: a failure fetching tickets must not be reported as a failed sale.
      let tickets = null;
      try {
        tickets = await fetchInvoiceTickets(invoice.id);
      } catch (ticketsError) {
        console.error("Error obteniendo tickets:", ticketsError);
        SnackbarUtils.warning('La venta se registró, pero no se pudieron obtener los tickets. Reimprimilos desde el historial de ventas.');
      }
      const ticketsData = tickets?.data?.data || tickets?.data || tickets || {};
      const facturaBase64 = ticketsData?.factura || null;
      const deliveryTicketBase64 = ticketsData?.deliveryTicket || null;
      const legacyExtraTickets = [
        ticketsData?.welcomeTicket,
        ticketsData?.loyaltyCardTicket,
        ticketsData?.postSaleTicket
      ].filter(Boolean);
      const extraTicketsBase64 = Array.isArray(ticketsData?.extraTickets) && ticketsData.extraTickets.length > 0
        ? ticketsData.extraTickets.map(ticket => ticket?.content).filter(Boolean)
        : legacyExtraTickets;
      const printableTickets = [
        facturaBase64,
        deliveryTicketBase64,
        ...extraTicketsBase64
      ].filter(Boolean);

      const openBase64Pdf = (base64) => {
        if (!base64) return;
        const blob = base64ToBlob(base64);
        const pdfUrl = URL.createObjectURL(blob);
        window.open(pdfUrl, '_blank');
        setTimeout(() => {
          try {
            URL.revokeObjectURL(pdfUrl);
          } catch (_) {
            // no-op
          }
        }, 60_000);
      };

      if (invoice.viewPrint) {
        try {
          printableTickets.forEach(openBase64Pdf);
        } catch (error) {
          console.error("Error mostrando tickets:", error);
        }
      }

      if (invoice.autoPrint) {
        try {
          for (const ticket of printableTickets) {
            await printBase64(ticket);
          }
        } catch (error) {
          console.error("Error en impresión de tickets:", error);
          // Continuar el flujo aunque falle la impresión
        }
      }

      const hasUndefined = items.filter(p => p.undefinedContent?.length > 0);

      if (hasUndefined.length > 0) {
        // Solo guardo las comandas, las imprimo luego en el modal
        setUndefinedProducts(hasUndefined);
        setShowUndefinedModal(true);    // Abro modal
        setLoading(false);
        onClose();
        return;
      }

      // Caso normal → imprimir comandas ahora
      if (invoice.comandaPrint && ticketsData?.comandas?.length) {
        try {
          for (const c of ticketsData.comandas) {
            await printBase64(c);
          }
        } catch (error) {
          console.error("Error en impresión de comandas:", error);
          // Continuar el flujo aunque falle la impresión
        }
      }

      finishSale();
      onClose();
    } catch (error) {
      console.error("Error general en handleConfirm:", error);
      const message =
        error?.response?.data?.error_message ||
        error?.response?.data?.message ||
        "Error al procesar la venta. Por favor, intente nuevamente.";
      if (approvedPayment.current || splitPartsRef.current.some((part) => part.transactionId)) {
        // The card was charged but the sale could not be created: back to the summary, where
        // retrying reuses that payment instead of charging again.
        setActiveStep(1);
        setPaymentNotice({
          severity: 'error',
          title: 'Cobro aprobado, pero no se pudo emitir la factura',
          message: `${message} El cobro ya se realizó en el POS: al reintentar no se volverá a cobrar.`,
        });
      } else {
        alert(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelFallback = () => {
    if (savedInvoice) {
      setPolling(false);
      if (savedInvoice.items.some(i => i.undefinedContent?.length > 0)) {
        setUndefinedProducts(savedInvoice.items.filter(i => i.undefinedContent?.length > 0));
        setShowUndefinedModal(true);
      } else finishSale();
      onClose();
    }
  };

  const finishSale = () => {
    resetForm();
    if (onFinish) onFinish();
  };

  const resetForm = () => {
    setActiveStep(0);
    setClientInfo({ ci: '', name: '', lastName: '', email: '', phone: '' });
    setCompanyInfo({
      ci: '',
      name: '',
      fiscalAddress: '',
      lookupStatus: 'idle',
      lookupSource: null,
      manualRegistrationConfirmed: false,
    });
    setErrors({});
    setSkipClient(false);
    setPaymentMethod('');
    setPaymentNotice(null);
    approvedPayment.current = null;
    setCashReceived('');
    setChosenPaymentCurrency(null);
      setStoreCreditCode('');
      setStoreCreditAmount(0);
      setStoreCreditError('');
      onStoreCreditConsumed?.();
    setSavedInvoice(null);
    setPolling(false);
    setPosTransaction(null);
    applySplitParts([]);
    setSplitMode(false);
  };

  useEffect(() => {
    const validOptions = paymentType.filter((opt) => opt.origin.includes(saleOrigin));
    const validIds = validOptions.map(opt => opt.id.toString());
    if (!validIds.includes(paymentMethod)) {
      setPaymentMethod('');
      setCashReceived('');
    }
  }, [saleOrigin]);

  // With an integrated POS, "Tarjeta" is the usual local payment: preselect it (also after any reset).
  useEffect(() => {
    if (posEnabled && amountDue > 0 && parseInt(saleOrigin) === SALE_ORIGIN.LOCAL && paymentMethod === '') {
      setPaymentMethod('2');
    }
  }, [posEnabled, amountDue, saleOrigin, paymentMethod]);

  // El pago dividido solo existe en ventas del local: al cambiar de origen se vuelve al pago con un solo medio.
  useEffect(() => {
    if (splitMode && parseInt(saleOrigin) !== SALE_ORIGIN.LOCAL && !splitPartsRef.current.some((part) => part.transactionId)) {
      applySplitParts([]);
      setSplitMode(false);
    }
  }, [saleOrigin]);

  useEffect(() => {
    if (!isCashPayment()) {
      setCashReceived('');
      if (activeStep === 2 && !isAsyncPayment()) {
        setActiveStep(1);
      }
    }
  }, [paymentMethod]);

  useEffect(() => {
    let interval = null;
    if (polling && savedInvoice) {
      interval = setInterval(async () => {
        const status = await getInvoiceStatus(savedInvoice.id);
        if (status === paymentStatus.PAID) {
          clearInterval(interval);
          setPolling(false);
          if (savedInvoice.items.some(i => i.undefinedContent?.length > 0)) {
            setUndefinedProducts(savedInvoice.items.filter(i => i.undefinedContent?.length > 0));
            setShowUndefinedModal(true);
          } else finishSale();
          onClose();
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [polling, savedInvoice]);

  useEffect(() => {
    if (activeStep === 2 && isAsyncPayment()) {
      setCountdown(10);
      setCanCancel(false);
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setCanCancel(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(countdownInterval);
    }
  }, [activeStep, paymentMethod, saleOrigin, mpActive]);

  // Resumen de lo que se envió al POS mientras se espera el cobro (un solo cobro, o cada parte del pago dividido).
  const awaitingSummary = () => {
    if (isSplit()) {
      return {
        rows: awaitingRows(splitParts, { posAvailable: posEnabled, currentUid: currentPosPartUid, ticketCurrency: currency, ticketRate }),
        total: saleTotal,
        ticketCurrency: currency,
      };
    }
    if (isPosCardPayment()) {
      return {
        rows: [{ uid: 'single', pos: true, state: approvedPayment.current ? 'approved' : 'current', label: 'Tarjeta (POS)', amount: paymentDue, currency: paymentCurrency }],
        total: saleTotal,
        ticketCurrency: currency,
      };
    }
    return null;
  };

  const steps = [
    'Cliente',
    'Resumen',
    ...(isCashPayment() ? ['Efectivo'] : []),
    ...(isAsyncPayment() ? ['Esperando pago'] : [])
  ];

  return (
    <>
      <Dialog open={open} onClose={undefined} maxWidth="sm" fullWidth>
        <DialogTitle>Facturación</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center">
            <Stepper activeStep={activeStep} sx={{ mb: 2, width: '100%' }}>
              {steps.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          </Box>

          {activeStep === 0 && (
            <StepClient {...{ clientInfo, companyInfo, errors, setClientInfo, setCompanyInfo, setErrors, skipClient, setSkipClient, needsCompany, setNeedsCompany, adenda, onAdendaChange: setAdenda, locationId }} />
          )}
          {activeStep === 1 && (
            <StepSummary {...{ lockOrigin, currency, items: effectiveItems, paymentMethod, setPaymentMethod, saleOrigin, setSaleOrigin, storeCreditAmount, storeCreditError, storeCreditLoading, amountDue, paymentCurrency, paymentDue, ticketRate, canChoosePaymentCurrency, mpQrSelected, onPaymentCurrencyChange: setChosenPaymentCurrency, cardViaPos: posEnabled, paymentNotice, onDismissNotice: () => setPaymentNotice(null), unbilledPayments, onUseUnbilled: useUnbilledPayment, splitAvailable: parseInt(saleOrigin) === SALE_ORIGIN.LOCAL && amountDue > 0, splitMode: isSplit(), onToggleSplit: toggleSplit, splitPanel: isSplit() ? <SplitPaymentPanel parts={splitParts} onPartsChange={applySplitParts} due={round2(amountDue)} currency={currency} ticketRate={ticketRate} posAvailable={posEnabled} unbilledPayments={unbilledPayments} onUseUnbilled={useUnbilledAsSplitPart} /> : null }} />
          )}
          {activeStep === 2 && isCashPayment() && (
            <StepCashPayment
              currency={paymentCurrency}
              total={paymentDue}
              ticketCurrency={currency}
              ticketTotal={amountDue}
              ticketRate={ticketRate}
              cashReceived={cashReceived}
              onCashReceivedChange={setCashReceived}
              parsedCashReceived={parsedCashReceived}
            />
          )}
          {activeStep === 2 && !isCashPayment() && <StepAwaitingPayment pos={(isPosCardPayment() && !approvedPayment.current) || splitHasPos()} sent={Boolean(posTransaction)} posProgress={posProgress} summary={awaitingSummary()} />}
        </DialogContent>

        <DialogActions>
          {activeStep === 0 && <Button onClick={onClose}>Cancelar</Button>}
          {activeStep === 0 && <Button onClick={handleSkipClient}>Saltar este paso</Button>}
          {activeStep === 0 && !skipClient && <Button variant="contained" onClick={handleNextStep}>Siguiente</Button>}
          {activeStep > 0 && activeStep < 2 && <Button onClick={() => setActiveStep(activeStep - 1)}>Atrás</Button>}
          {activeStep === 1 && isAsyncPayment() && <LoadingButton variant="contained" onClick={handleNextStep} loading={loading} disabled={!paymentSelectionComplete}>Siguiente</LoadingButton>}
          {activeStep === 1 && isCashPayment() && <LoadingButton variant="contained" onClick={handleNextStep} loading={loading} disabled={!paymentSelectionComplete}>Siguiente</LoadingButton>}
          {activeStep === 1 && !isAsyncPayment() && !isCashPayment() && <LoadingButton variant="contained" onClick={handleConfirm} loading={loading} disabled={!paymentSelectionComplete}>Confirmar</LoadingButton>}
          {activeStep === 2 && isCashPayment() && <Button onClick={() => setActiveStep(1)}>Atrás</Button>}
          {activeStep === 2 && isCashPayment() && (
            <LoadingButton
              variant="contained"
              onClick={handleConfirm}
              loading={loading}
              disabled={!canConfirmCashPayment}
            >
              Confirmar
            </LoadingButton>
          )}
          {/* A card payment on the POS can only be cancelled from the POS itself, but once the invoice
              is saved there is nothing left to wait for: keep an exit if a later step (tickets) failed. */}
          {activeStep === 2 && !isCashPayment() && (!(isPosCardPayment() || splitHasPos()) || savedInvoice) && (
            <Button onClick={handleCancelFallback} disabled={!canCancel && !(isPosCardPayment() || splitHasPos())}>
              {canCancel || isPosCardPayment() || splitHasPos() ? 'Cerrar' : `Cerrar (${countdown})`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <UndefinedContentModal
        open={showUndefinedModal}
        invoice={savedInvoice}
        locationId={locationId}
        onClose={async (resolved) => {
          setShowUndefinedModal(false);
          if (resolved && savedInvoice) {
            // 🖨️ Ahora imprimir comandas si había
            const tickets = await fetchInvoiceTickets(savedInvoice.id);
            const data = tickets?.data ?? tickets;
            if (savedInvoice.comandaPrint && data?.comandas?.length) {
              for (const c of data.comandas) {
                await printBase64(c);
              }
            }
            finishSale();
          }
        }}
      />
    </>
  );
};

export default BillingModal;
